import React from 'react'
import { useIntl } from 'react-intl'
import { css, cx } from '@emotion/css'
import { CsFilterIcon } from '../../../../legacy-design-system/icons'
import { theme as deprecatedTheme } from '../../../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  Filters,
  OnGroupToggleProps,
  OnOptionToggleProps,
  OnMultiGroupToggleProps
} from '../../../../legacy-design-system/filter'
import { getAggregationFilters } from '../../../../legacy-design-system/grid'
import {
  areSomeFiltersActive,
  ClearFilterContent
} from '../../../../legacy-design-system/grid/column-header'
import { VrApplicationAggregations } from '.'
import { FilterConfigs } from '../../../../legacy-design-system/filter/filters'
import { AggregationOptions, AggregationConfigs } from '../../../aggregations'
import { LoadingMaskRectangle } from '../../../../legacy-design-system/loading-masks'
import { SelectedValueReportingFacet } from '../../../../graphql/schema/types/value-reporting'
import { useApplicationSecuritySummaryFacets } from '../../api/use-application-security-summary-facets'
export const applicationFiltersLabelTestId = 'application-filters-label-test-id'

const cssFilterLabel = css`
  display: flex;
  align-items: center;
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
  font-size: 14px;
  width: fit-content;
`

const cssFilterLabelClickable = cx(
  cssFilterLabel,
  css`
    cursor: pointer;
  `
)

const cssLoadingMask = css`
  text-transform: uppercase;
`

const cssFiltersContainer = css`
  min-width: 175px;
  margin-right: 30px;
`

const cssFilterIcon = css`
  margin: 0 0 5px 5px;
`

const cssFilterIconSelected = cx(
  cssFilterIcon,
  css`
    color: ${theme.color.turquoise70};
  `
)

const valueReportingApplicationAggregationConfigs: AggregationConfigs<VrApplicationAggregations> =
  {
    applicationTags: {
      groupName: { id: 'title_tags_application' },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    },
    agentLanguage: {
      groupName: { id: 'title_programmingLanguages' },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    },
    applicationTechnologies: {
      groupName: { id: 'title_technologies' },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    },
    applicationImportance: {
      groupName: { id: 'title_appImportances' },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    }
  }

const aggregationConfigs = {
  ...valueReportingApplicationAggregationConfigs
}

interface VrApplicationFilters {
  readonly groupConfigs: FilterConfigs
  readonly selections: AggregationOptions<VrApplicationAggregations>
  readonly groupIDs: VrApplicationAggregations[]
  readonly onMultiGroupToggle: (props: OnMultiGroupToggleProps<string>) => void
  readonly onGroupToggle: (props: OnGroupToggleProps<string>) => void
  readonly onOptionToggle: (props: OnOptionToggleProps<string>) => void
}

function ApplicationFilterButton({
  loading,
  totalCount,
  filters,
  isFiltered,
  onAggregationCategoriesClear
}: {
  readonly loading: boolean
  readonly totalCount?: number
  readonly filters: VrApplicationFilters
  readonly isFiltered: boolean
  readonly onAggregationCategoriesClear: (categories: string[]) => void
}) {
  const intl = useIntl()

  return (
    <div
      data-e2e="value-reporting-application-filters"
      data-testid={applicationFiltersLabelTestId}
      className={!loading ? cssFilterLabelClickable : cssFilterLabel}
    >
      <div className={cssLoadingMask}>
        {loading ? (
          <LoadingMaskRectangle
            dataTestId="vr-application-filter-button-loading-mask"
            width={94}
            height={15}
          />
        ) : (
          intl.formatMessage(
            { id: 'title_applicationsLicensedPlural' },
            { count: totalCount || 0 }
          )
        )}
      </div>
      <div>
        <CsFilterIcon
          className={isFiltered ? cssFilterIconSelected : cssFilterIcon}
        />
      </div>
      {isFiltered && (
        <div
          className={css`
            display: inline;
          `}
        >
          <ClearFilterContent
            className={css`
              display: inline;
            `}
            onClick={() => onAggregationCategoriesClear(filters.groupIDs)}
          />
        </div>
      )}
    </div>
  )
}

export interface VrFilterManagerChildProps<
  TAggregationCategory extends string
> {
  readonly onGroupToggle: (
    props: OnGroupToggleProps<TAggregationCategory>
  ) => void
  readonly onMultiGroupToggle: (props: OnMultiGroupToggleProps<string>) => void
  readonly onOptionToggle: (
    props: OnOptionToggleProps<TAggregationCategory>
  ) => void
  readonly aggregationSelections: AggregationOptions<TAggregationCategory>
  readonly selectedFacets: SelectedValueReportingFacet[]
  readonly onAggregationCategoriesClear: (
    categories: TAggregationCategory[]
  ) => void
}

export function VrApplicationFilter({
  organizationUuid,
  aggregationSelections,
  selectedFacets,
  ratingsLoading,
  totalApplications,
  onGroupToggle,
  onMultiGroupToggle,
  onOptionToggle,
  onAggregationCategoriesClear
}: VrFilterManagerChildProps<VrApplicationAggregations> & {
  readonly organizationUuid: string
  readonly ratingsLoading: boolean
  readonly totalApplications?: number
}) {
  const {
    isLoading: aggregationsIsLoading,
    data: { aggregations, applicationSecuritySummaryFacetsRequest }
  } = useApplicationSecuritySummaryFacets({
    organizationUuid,
    selectedFacets
  })

  const filters: VrApplicationFilters = {
    groupConfigs: getAggregationFilters({
      aggregations,
      aggregationConfigs: Object.assign(
        applicationSecuritySummaryFacetsRequest,
        aggregationConfigs
      )
    }),
    selections: aggregationSelections,
    groupIDs: aggregations
      ? (Object.keys(aggregations) as VrApplicationAggregations[])
      : [],
    onMultiGroupToggle,
    onGroupToggle,
    onOptionToggle
  }

  const isFiltered = areSomeFiltersActive<VrApplicationAggregations>(filters)

  const isLoading = ratingsLoading && aggregationsIsLoading

  return (
    <div className={cssFiltersContainer}>
      <Filters
        groupConfigs={filters.groupConfigs}
        selections={aggregationSelections}
        groupIDs={filters.groupIDs}
        onMultiGroupToggle={onMultiGroupToggle}
        onGroupToggle={onGroupToggle}
        onOptionToggle={onOptionToggle}
        isClickable={!isLoading}
        e2eTestId="value-reporting-application-filters-menu"
      >
        {() => (
          <ApplicationFilterButton
            loading={isLoading}
            totalCount={totalApplications}
            filters={filters}
            isFiltered={isFiltered}
            onAggregationCategoriesClear={onAggregationCategoriesClear}
          />
        )}
      </Filters>
    </div>
  )
}
